/**
 * AiTools — CM AI Tools portal landing page (Phase X.3.1 → X.6.4 → B1).
 *
 * X.6.4 "Warm" restyle stripped the cards to title + dove-chip only (no
 * per-tool icon, no description). B1 (operator, 2026-06-08) REVERSES that FOR
 * THIS LANDING ONLY (the hub tiles stay bare): each card carries a per-tool
 * Lucide icon + a one-line description, the not-yet-live Intake tool is no
 * longer surfaced, and the eyebrow + Playfair "AI Tools" title now sit on a
 * slim forest band (cream text), pairing with the Directory wide band. The
 * ghost "My Drafts" pill (gold count badge) + the auto-fill card grid + the
 * quiet centered footnote carry over. x.6.1 tokens throughout; zero raw hexes.
 *
 * Founder walk (2026-07-06): the hand-rolled band migrates onto the
 * SectionBand primitive (#445) — the purpose line is BAND content per DL1
 * (it had been orphaned below the band in a narrow two-line column) and
 * the My Drafts pill takes the band's top-right slot.
 *
 * Capability gate (canUseAiTools) unchanged. Titles stay role/tenant-neutral.
 */

import { type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FileText, Mail, ClipboardList, PenLine, Wand2, Sparkles, Pill, type LucideIcon } from 'lucide-react';
import { useTenant } from '@/lib/auth';
import { canUseAiTools, canWriteClinicalRecord } from '@/lib/capabilities';
import { aiToolsApi } from '@/lib/api';
import { slugToToolKey, useDisabledAiTools } from '@/lib/aiToolToggles';
import { AiToolCard } from '@/components/aiTools/AiToolCard';
import { EmptyAiToolsTile } from '@/components/aiTools/EmptyAiToolsTile';
import { HubBrandMark } from '@/components/hub/HubBrandMark';
import { CountBadge } from '@/components/primitives/CountBadge';
import { PAGE_GUTTER, PAGE_WIDTH_CLASSES } from '@/components/primitives/PageShell';
import { SectionBand } from '@/components/primitives/SectionBand';

export type AiToolSlug =
  | 'email'
  | 'assessment'
  | 'assessment-letter'
  | 'brand-formatter'
  | 'med-list'
  | 'intake';
export type AiToolStatus = 'available' | 'coming-soon';

export interface AiToolMeta {
  slug: AiToolSlug;
  title: string;
  /** One-line description of what the tool does (B1; role/tenant-neutral). */
  description: string;
  /** A second tile line naming the tool's SPICE mode, when it has one.
   *  Undefined for every tool without one — role/tenant-neutral, same as
   *  `description`, not conditioned on the viewer. */
  spiceLine?: string;
  /** Per-tool Lucide icon (B1; NOT the dove on every card). */
  icon: LucideIcon;
  status: AiToolStatus;
}

// B1: each card carries a distinct Lucide icon + a plain one-line
// description. The not-yet-live Intake tool stays in this registry (its
// per-tool shell resolves its meta from here by slug), but the LANDING
// renders only live tools — see LIVE_AI_TOOLS below.
export const AI_TOOLS: ReadonlyArray<AiToolMeta> = [
  {
    slug: 'email',
    title: 'Email Helper',
    description: 'Draft emails in the Peace voice.',
    spiceLine: 'Write the SPICE email summary, then file it as the session note.',
    icon: Mail,
    status: 'available',
  },
  {
    slug: 'assessment',
    title: 'Formal Assessment Report',
    description: 'Turn visit notes into a branded assessment report.',
    icon: ClipboardList,
    status: 'available',
  },
  {
    slug: 'assessment-letter',
    title: 'Summary Assessment Letter',
    description: 'Write the family a letter after an assessment visit.',
    icon: PenLine,
    status: 'available',
  },
  {
    slug: 'brand-formatter',
    title: 'Brand Formatter',
    description: 'Clean up and brand-format pasted text.',
    icon: Wand2,
    status: 'available',
  },
  {
    slug: 'med-list',
    title: 'Medication List',
    description: 'Build a medication list in the company template.',
    icon: Pill,
    status: 'available',
  },
  {
    slug: 'intake',
    title: 'Intake Summary Generator',
    description: 'Generate an intake summary from your notes.',
    icon: Sparkles,
    status: 'coming-soon',
  },
];

/** The landing surfaces only live tools (B1: drop the coming-soon Intake). */
export const LIVE_AI_TOOLS: ReadonlyArray<AiToolMeta> = AI_TOOLS.filter(
  (tool) => tool.status === 'available',
);

/**
 * Ghost pill (paper, forest hairline) + a gold count badge linking to the
 * drafts inbox. The count is a nice-to-have: render the pill without the badge
 * if the count is unavailable (loading / error), per the "degrade gracefully"
 * disposition. retry:false fails fast to the no-badge fallback.
 */
function MyDraftsPill({ tenantSlug }: { tenantSlug: string }): ReactNode {
  const query = useQuery({
    queryKey: ['ai-tools', 'drafts', 'count', tenantSlug],
    queryFn: () => aiToolsApi.listDrafts(tenantSlug),
    staleTime: 30_000,
    retry: false,
  });

  const count = !query.isLoading && !query.isError ? (query.data?.rows.length ?? null) : null;

  return (
    <Link
      to={`/t/${tenantSlug}/ai-tools/drafts`}
      data-testid="my-drafts-pill"
      className="inline-flex items-center gap-2 rounded-pill border border-sage-800/[8%] bg-paper px-4 py-2 text-sm font-semibold text-sage-800 shadow-sh1 transition-all duration-[var(--dur-base)] ease-quiet hover:border-sage-400/20 hover:shadow-sh2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
    >
      <FileText size={16} aria-hidden="true" className="text-sage-400" />
      <span>My Drafts</span>
      {count !== null && (
        // DL5: the one badge shape — the gold circle (CountBadge).
        <CountBadge
          data-testid="my-drafts-count"
          size="md"
          aria-label={`${count} saved drafts`}
        >
          {count}
        </CountBadge>
      )}
    </Link>
  );
}

export function AiTools(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const capEligible = canUseAiTools(whoami);

  // Drop tenant-disabled tools from the landing. Fail-open UX: while loading
  // or on error the set is empty, so all live tools show (the server 403 in
  // the handlers is the authoritative gate). The read is the one the photo
  // doors share (useDisabledAiTools): held five minutes, no retry.
  const disabledKeys = useDisabledAiTools(tenantSlug, capEligible);
  // The Medication List entry follows the record card's "Make med
  // list" door (canWriteClinicalRecord), so a SPICE-only therapist, who
  // reads a client's medications but does not keep them, has neither.
  const keepsRecord = canWriteClinicalRecord(whoami);
  const visibleTools = LIVE_AI_TOOLS.filter(
    (tool) => !disabledKeys.has(slugToToolKey(tool.slug)) && (tool.slug !== 'med-list' || keepsRecord),
  );

  if (!capEligible) {
    return (
      <main className={`${PAGE_WIDTH_CLASSES.standard} mx-auto ${PAGE_GUTTER} py-8`}>
        <EmptyAiToolsTile />
      </main>
    );
  }

  return (
    <div data-testid="ai-tools-main">
      {/* Founder walk 2026-07-06: the SECTION band primitive (#445) —
          forest, gold eyebrow, serif title, the purpose line IN the band
          (one line at the band's content width), My Drafts top right. */}
      <SectionBand
        testId="ai-tools-band"
        width="standard"
        eyebrow="Olive · AI"
        title="Writing"
        action={<MyDraftsPill tenantSlug={tenantSlug} />}
      />

      {/* DL9: the body takes the STANDARD width token + the shared page
          gutter — same container as the band frame above (alignment
          hotfix: one left edge per page). */}
      <main
        data-testid="ai-tools-body"
        className={`${PAGE_WIDTH_CLASSES.standard} mx-auto ${PAGE_GUTTER} pt-8 pb-20`}
      >
        <div
          className="grid gap-4"
          style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(min(290px, 100%), 1fr))' }}
        >
          {visibleTools.map((tool) => (
            <AiToolCard key={tool.slug} tool={tool} tenantSlug={tenantSlug} />
          ))}
        </div>

        {/* 2026-06-09: the "Everything saves as a draft" caption was dropped
            per operator; the quiet sage dove is the only mark now, slightly
            larger and centered. */}
        <div className="mt-7 flex items-center justify-center">
          <HubBrandMark
            label=""
            className="flex w-6 [&>svg]:h-auto [&>svg]:w-full"
            style={{ color: 'var(--color-sage-300)' }}
          />
        </div>
      </main>
    </div>
  );
}
